import React, { useState } from 'react';
import { X, Loader, ArrowRight } from 'lucide-react';
import { moveProcessService } from '../../../../services/moveProcessService';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface AddStepModalProps {
    processId: number;
    isBusy: boolean;
    onClose: () => void;
    onAdded: () => void;
}

export const AddStepModal: React.FC<AddStepModalProps> = ({ processId, onClose, onAdded }) => {
    const { t } = useTranslation();
    const [fromBin, setFromBin] = useState('');
    const [toBin, setToBin] = useState('');
    const [itemNo, setItemNo] = useState('');
    const [quantity, setQuantity] = useState<number>(0);
    const [batchNo, setBatchNo] = useState('');
    const [serialNo, setSerialNo] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canAdd = !busy
        && fromBin.trim().length > 0
        && toBin.trim().length > 0
        && itemNo.trim().length > 0
        && quantity > 0
        && fromBin.trim().toLowerCase() !== toBin.trim().toLowerCase();

    const handleAdd = async () => {
        setBusy(true);
        setError(null);
        try {
            await moveProcessService.addStep(processId, {
                fromBinCode: fromBin.trim(),
                toBinCode: toBin.trim(),
                itemNo: itemNo.trim(),
                quantity,
                batchNo: batchNo.trim() || undefined,
                serialNo: serialNo.trim() || undefined
            });
            onAdded();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('manager.modules.moveProcesses.addStepModal.error', {}, 'Kunne ikke legge til steg'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-bold">{t('manager.modules.moveProcesses.addStepModal.title', {}, 'Legg til flyttesteg')}</h2>
                    <button onClick={onClose} disabled={busy} className="p-1 rounded hover:bg-gray-100 disabled:opacity-50">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>
                <div className="px-6 py-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">{t('manager.modules.moveProcesses.addStepModal.fromBinLabel', {}, 'Fra-lokasjon *')}</label>
                            <input
                                type="text"
                                value={fromBin}
                                onChange={e => setFromBin(e.target.value)}
                                maxLength={30}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                                placeholder="010101"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">{t('manager.modules.moveProcesses.addStepModal.toBinLabel', {}, 'Til-lokasjon *')}</label>
                            <input
                                type="text"
                                value={toBin}
                                onChange={e => setToBin(e.target.value)}
                                maxLength={30}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                                placeholder="020305"
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-center text-gray-400">
                        <ArrowRight className="w-5 h-5" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">{t('manager.modules.moveProcesses.addStepModal.itemNoLabel', {}, 'Varenr. *')}</label>
                            <input
                                type="text"
                                value={itemNo}
                                onChange={e => setItemNo(e.target.value)}
                                maxLength={50}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">{t('manager.modules.moveProcesses.addStepModal.quantityLabel', {}, 'Mengde *')}</label>
                            <input
                                type="number"
                                min={0}
                                step="any"
                                value={quantity}
                                onChange={e => setQuantity(parseFloat(e.target.value) || 0)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">{t('manager.modules.moveProcesses.addStepModal.batchLabel', {}, 'Batch (valgfritt)')}</label>
                            <input
                                type="text"
                                value={batchNo}
                                onChange={e => setBatchNo(e.target.value)}
                                maxLength={100}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">{t('manager.modules.moveProcesses.addStepModal.serialLabel', {}, 'Serienr. (valgfritt)')}</label>
                            <input
                                type="text"
                                value={serialNo}
                                onChange={e => setSerialNo(e.target.value)}
                                maxLength={100}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                            />
                        </div>
                    </div>
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-800">
                            {error}
                        </div>
                    )}
                </div>
                <div className="flex justify-end space-x-2 px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                    <button onClick={onClose} disabled={busy} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold">
                        {t('common.buttons.cancel', {}, 'Avbryt')}
                    </button>
                    <button
                        onClick={handleAdd}
                        disabled={!canAdd}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center space-x-2"
                    >
                        {busy && <Loader className="w-4 h-4 animate-spin" />}
                        <span>{t('common.buttons.add', {}, 'Legg til')}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
